import React, { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "./Header";
import Footer from "./Footer";

const Layout = ({ children, darkMode, setDarkMode }) => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [showMessageTooltip, setShowMessageTooltip] = useState(true);
  const { pathname } = useLocation();

  // WhatsApp number
  const whatsappNumber = "+201114779577";
  const whatsappMessage =
    "Hello! I'm interested in your products. Can you help me?";
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;

  // Auto scroll to top on path change with smooth behavior
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);

  // Hide message tooltip after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowMessageTooltip(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Track scroll progress for reading indicator and back to top button
  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
      setShowBackToTop(window.scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Check if we're on the home page
  const isHomePage = pathname === "/";
  const isAuthPage =
    pathname.includes("/login") || pathname.includes("/register");
  const isAdminPage = pathname.includes("/admin");

  // Page transition variants
  const pageVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.645, 0.045, 0.355, 1],
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3,
        ease: [0.645, 0.045, 0.355, 1],
      },
    },
  };

  // Generate breadcrumb items
  const getBreadcrumbs = () => {
    const paths = pathname.split("/").filter(Boolean);
    const breadcrumbs = [{ name: "Home", path: "/" }];

    let currentPath = "";
    paths.forEach((segment, index) => {
      currentPath += `/${segment}`;
      breadcrumbs.push({
        name: segment.charAt(0).toUpperCase() + segment.slice(1),
        path: currentPath,
        isLast: index === paths.length - 1,
      });
    });

    return breadcrumbs;
  };

  // Get page title
  const getPageTitle = () => {
    const path = pathname.split("/")[1];
    const titles = {
      "": "Home",
      products: "Products",
      product: "Product Details",
      about: "About Us",
      contact: "Contact",
      blog: "Blog",
      inquiry: "New Inquiry",
      user: "My Account",
      admin: "Admin Dashboard",
    };
    return titles[path] || "TradeBridge";
  };

  // Update document title
  useEffect(() => {
    document.title = `${getPageTitle()} | TradeBridge - Global B2B Marketplace`;
  }, [pathname]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 relative">
      {/* Scroll Progress Indicator */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-500 to-secondary-500 z-50 origin-left"
        style={{ scaleX: scrollProgress / 100 }}
        initial={{ scaleX: 0 }}
        animate={{ scaleX: scrollProgress / 100 }}
        transition={{ duration: 0.1 }}
      />

      {/* Background Pattern */}
      <div className="fixed inset-0 pointer-events-none opacity-5 dark:opacity-10">
        <div className="absolute inset-0 bg-grid-pattern" />
      </div>

      {/* Floating Header */}
      <Header darkMode={darkMode} setDarkMode={setDarkMode} />

      {/* Main Content Area */}
      <main className="relative z-10">
        {/* Page Transition Wrapper */}
        <AnimatePresence mode="wait">
          <motion.div
            key={pathname}
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
          >
            {/* Conditional Container Styling */}
            {isHomePage ? (
              // Home Page Layout - Full width with hero section integration
              <div className="bg-white/95 dark:bg-gray-950/95 backdrop-blur-sm rounded-t-3xl mt-[-2px] relative">
                {/* Decorative Top Border */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent" />
                {children}
              </div>
            ) : isAuthPage ? (
              // Auth Pages - Centered with no extra padding
              <div className="min-h-screen flex items-center justify-center py-12">
                <div className="w-full max-w-md mx-auto">{children}</div>
              </div>
            ) : isAdminPage ? (
              // Admin Pages - Full width with minimal padding
              <div className="pt-20 pb-8">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                  {children}
                </div>
              </div>
            ) : (
              // Regular Pages - Standard container with breadcrumb
              <div className="pt-8 pb-12">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                  {/* Breadcrumb Navigation - Clickable */}
                  <nav className="flex items-center gap-2 text-sm mb-6 text-gray-500 dark:text-gray-400 flex-wrap">
                    {getBreadcrumbs().map((item, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && (
                          <svg
                            className="w-4 h-4 text-gray-400 dark:text-gray-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        )}
                        {item.isLast ? (
                          <span className="text-gray-900 dark:text-white font-medium">
                            {item.name}
                          </span>
                        ) : (
                          <Link
                            to={item.path}
                            className="hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200"
                          >
                            {item.name}
                          </Link>
                        )}
                      </React.Fragment>
                    ))}
                  </nav>

                  {/* Page Content */}
                  {children}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Floating Footer */}
      <Footer />

      {/* Floating Action Buttons - Right side */}
      <div className="fixed bottom-8 right-4 md:bottom-8 md:right-8 z-40 flex flex-col items-center gap-4">
        {/* Message Button */}
        <div className="relative">
          {/* Tooltip */}
          <AnimatePresence>
            {showMessageTooltip && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                transition={{ delay: 0.5 }}
                className="absolute bottom-full right-0 mb-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white px-4 py-2 rounded-lg shadow-lg text-sm whitespace-nowrap"
              >
                <p className="font-medium">Need help? Chat with us!</p>
                <div className="absolute bottom-0 right-4 translate-y-1/2 rotate-45 w-3 h-3 bg-white dark:bg-gray-800"></div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Message Link Button */}
          <motion.a
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onMouseEnter={() => setShowMessageTooltip(true)}
            onMouseLeave={() => setShowMessageTooltip(false)}
            className="flex items-center justify-center w-14 h-14 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 text-white rounded-full shadow-lg transition-all duration-300 group relative"
            aria-label="Send us a message"
          >
            {/* Pulse Animation */}
            <span className="absolute inset-0 rounded-full bg-primary-500 animate-ping opacity-25"></span>

            {/* Message Icon */}
            <svg
              className="w-6 h-6 relative z-10 group-hover:scale-110 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
          </motion.a>
        </div>

        {/* Back to Top Button */}
        <AnimatePresence>
          {showBackToTop && (
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="p-3 bg-gray-800 dark:bg-gray-200 hover:bg-gray-700 dark:hover:bg-gray-300 text-white dark:text-gray-800 rounded-full shadow-lg transition-all duration-300 group"
              aria-label="Back to top"
            >
              <svg
                className="w-5 h-5 group-hover:-translate-y-0.5 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 10l7-7m0 0l7 7m-7-7v18"
                />
              </svg>
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Add custom styles for grid pattern */}
      <style jsx>{`
        .bg-grid-pattern {
          background-image:
            linear-gradient(to right, rgba(0, 0, 0, 0.1) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }

        .dark .bg-grid-pattern {
          background-image:
            linear-gradient(
              to right,
              rgba(255, 255, 255, 0.05) 1px,
              transparent 1px
            ),
            linear-gradient(
              to bottom,
              rgba(255, 255, 255, 0.05) 1px,
              transparent 1px
            );
        }
      `}</style>
    </div>
  );
};

export default Layout;
